/**
 * The start-screen Multiplayer section (multiplayer-entry-and-notifications design § "The
 * section"): a full-width quiet vellum bar — the multiplayer mark, the "Multiplayer" label, a
 * chevron that flips on expand — mounted between the mode cards and the control bank
 * (game-start-ui.js#buildGameStartContent). Collapsed on EVERY mount: expanding it is always a
 * deliberate act (Andre, 2026-08-22), either a bar click (`trackSource: "bar"`) or the in-game
 * Menu's one-shot (`trackSource: "menu"`, below). Nothing is remembered across mounts — the
 * `localStorage` state this module used to keep is retired (`legacy-storage-sweep.js` cleans the
 * dead key out of long-lived browsers) — and nothing opens the section on the viewer's behalf:
 * the bar's amber count badge is the collapsed section's own signal that tables await, and it
 * suffices.
 *
 * Since start-screen-layout-improvements part B, the panel carries two PERMANENT children, both
 * section-owned: `.mp-panel-notice` (the your-turn banner block's home — only the turn-
 * notification flow below touches it) and `.mp-panel-body` (the fetch/nudge/error mount target —
 * every content path below wipes ITS target, `replaceChildren`-first, so the notice block
 * survives every refresh by construction; the round-1 blocking finding this structure fixes).
 * `buildMultiplayerSection(refreshNameSlot, pristine)` — the second `pristine` param, threaded
 * from `buildGameStartContent` the same way `game-start-turn-banners.js`'s own pristine/non-
 * pristine split used to be threaded to it directly — decides which of that module's two data
 * sources feeds the turn-notification flow: `readStampTurnRuns()` synchronously on the pristine
 * first paint, `fetchTurnRuns()` on every later render.
 *
 * Expanded (registered), the body fetches `multiplayer :: tables` on every expand (no caching —
 * the page's own reload-is-the-refresh stance) via `fragment-loader.js`, then enhances it with
 * `multiplayer-tables.js#enhanceTables`, `onEnter` = `enterRun` (a package-internal import — no
 * boundary crossing). Expanded (unregistered), a client-built nudge line + CTA opens the profile
 * overlay (`game-start-profile-open.js#openProfileOverlay`); its success callback (`rerenderSlot`)
 * refetches the body, so tables appear in place
 * after registering — no reload. The dashed "open a new table" row is section-owned client-built
 * DOM in both cases (not template markup — it only exists on this screen, § "The section": putting
 * it in `:: tables` would silently add a self-linking row to the standalone page); since PR 2 it
 * opens a vellum overlay (`openCreateOverlay`, overlay-shell.js) fetching `multiplayer :: create`
 * and wiring it with `wireCreateForm` — success dismisses the overlay and `enterRun`s the fresh
 * table in-page, replacing PR 1's navigate-to-`/multiplayer` stopgap.
 *
 * The panel sits **outside the height solve**: `MULTIPLAYER_PANEL_SELECTOR` is the marker class
 * `game-start-layout.js#measureMenuH` subtracts from the measured column (covering both children
 * — `offsetHeight` on the panel node includes them), so expansion never re-solves the composition
 * and never shrinks the mode cards / Play button — including the free side effect the turn-
 * notification move buys: the notice block itself never inflates the solve either. Expand/collapse
 * is a plain measure-then-transition `max-height` animation — unstarved by design, since no solve
 * fires on it (§ "The section").
 *
 * `requestMultiplayerSectionOpen()` is a one-shot flag: the in-game Menu's "Multiplayer" entry
 * (`game-menu.js`, via `game-start-ui.js`'s re-export) sets it to force the section open on the
 * screen's next mount. That's a control interaction like the bar click, so it tracks
 * (`trackSource: "menu"`) — and it's one-shot rather than sticky for the same reason the whole
 * section is: using Menu once shouldn't strand the section open on a later visit that never
 * touched it.
 */
import { t } from "../i18n.js";
import { getRegisteredName } from "./game-start-auth.js";
import { openProfileOverlay } from "./game-start-profile-open.js";
import { enterRun } from "./game-start-session.js";
import { fetchFragment, mountFragment } from "../fragment-loader.js";
import { enhanceTables, wireCreateForm } from "../multiplayer-tables.js";
import { openVellumOverlay } from "../overlay-shell.js";
import { createThreeLeggedStool } from "../icons/nav-icons.js";
import { createPersonaKnight } from "../icons/persona-icons.js";
import { track } from "../analytics.js";
import {
  readStampTurnRuns,
  fetchTurnRuns,
  buildTurnBannerBlock,
  turnCountSentence,
} from "./game-start-turn-banners.js";

/** The marker class `game-start-layout.js#measureMenuH` reads to exclude this panel from the
 *  height solve (§ "The section" — the as-if-collapsed subtraction). Collapsed, the panel's
 *  `offsetHeight` is 0 (a no-op subtraction); expanded, it's the panel's real content height —
 *  both children's, notice and body alike — so the solver always sizes the rest of the
 *  composition as though the panel were collapsed. */
export const MULTIPLAYER_PANEL_SELECTOR = ".mp-section-panel";

// The mark's build-time SVG attr size — overridden by CSS (`.mp-section-mark svg`, skat.css),
// the real elastic sizing source since start-screen-unfolded-menu-strip's "Multiplayer bar joins
// the strip's scale": the mark's base px matches the strip's own icon base (game-start-reference-
// strip.js#ICON_BUILD_SIZE), so the old fixed-28px constant here — which the CSS overrode
// anyway — retired. `.mp-section-mark svg`'s own comment (skat.css) is the full account of why
// this pre-CSS fallback isn't "the" size's one home (review finding) — this file only needs the
// pointer, not a second enumeration.
const MARK_BUILD_SIZE = 44;
// why: smaller than the 24px menu-entry icon — this sits inline in a compact button, not a full-width row
const NUDGE_CTA_ICON_SIZE = 20;

let forceOpenOnce = false;

// A rapid expand/collapse/expand double-toggle can leave two `/multiplayer/tables-panel`
// fetches in flight; whichever resolves last would otherwise win the DOM regardless of which
// was requested last (review finding — the same stale-response race leaderboard/leaderboard.js's
// own `requestSeq` already guards against, over there a per-container counter, since two boards
// can be live at once).
// Only one multiplayer section is ever mounted at a time, so a monotonic module counter (not a
// per-panel token) is enough: a fetch checks it's still the latest before mounting.
let fetchSeq = 0;

/** Forces the section open on its next mount (the in-game Menu's "Multiplayer" entry) — a one-shot
 *  flag: using Menu once shouldn't strand the section open on a later visit that never touched
 *  it. Consumed (read and cleared) by the next `buildMultiplayerSection` call. */
export function requestMultiplayerSectionOpen() {
  forceOpenOnce = true;
}

function isOpen(bar) {
  return bar.getAttribute("aria-expanded") === "true";
}

/** Pins the panel's max-height to its current real content (0 on a still-empty pre-fetch arm),
 *  driving the CSS transition, then arms a one-shot settle that clears the inline cap to `none`
 *  once the transition ends — so a later reflow (viewport resize) isn't capped at a stale pixel
 *  value. No stillborn-transition watchdog (contrast leaderboard-expansion.js's
 *  `clearMaxHeightAfterSettle`): that fallback exists there because the leaderboard's expansion
 *  can get starved by `game-start-layout.js`'s `.gs-solving` freeze mid-transition — this panel is
 *  structurally excluded from that solve (`MULTIPLAYER_PANEL_SELECTOR` above), so no solve ever
 *  fires on it and the transition is never starved.
 *
 *  No-ops when already at `max-height: none` (review finding, both rounds): every caller here
 *  (`expand`'s two arms, the identity-overlay success callback, `applyTurns`'s already-open
 *  branch) can fire once the panel already reached that steady, uncapped state — a slower
 *  fetch resolving after the prior settle's own transition already cleared the cap, e.g. Pinning
 *  to `scrollHeight` there produces the box's already-current rendered height (nothing was
 *  capping it), a no-op value change — confirmed live in Chromium: `max-height: none -> <px>`
 *  never fires `transitionend` when the box's rendered height doesn't move, so the pin would
 *  strand `max-height` at that pixel value, clipping whatever grows the panel next. Centralized
 *  here rather than guarded per call site — one knowledge unit, every caller protected, no
 *  caller needs to reason about the panel's current transition state. */
function settleOpen(panel) {
  if (panel.style.maxHeight === "none") return;
  panel.style.maxHeight = `${panel.scrollHeight}px`;
  const onSettle = (e) => {
    if (e.target !== panel || e.propertyName !== "max-height") return;
    panel.removeEventListener("transitionend", onSettle);
    if (panel.classList.contains("mp-section-panel--open")) {
      panel.style.maxHeight = "none";
    }
  };
  panel.addEventListener("transitionend", onSettle);
}

function expand(bar, panel, refreshNameSlot, trackSource) {
  bar.setAttribute("aria-expanded", "true");
  panel.classList.add("mp-section-panel--open");
  panel.setAttribute("aria-hidden", "false");
  track("multiplayer-section-opened", { source: trackSource });
  settleOpen(panel); // pre-fetch arm: whatever the panel already holds (empty on a first open)
  refreshPanel(panel, bar, refreshNameSlot).then(() => {
    if (!isOpen(bar)) return; // collapsed again before the fetch/build landed
    settleOpen(panel); // re-measure now that fresh content is mounted
  });
}

function collapse(bar, panel) {
  bar.setAttribute("aria-expanded", "false");
  panel.setAttribute("aria-hidden", "true");
  // Re-pin the current pixel height first (may already be `none`) so the drop to 0 has a
  // <length> to transition FROM — `none -> 0` doesn't animate. Force a layout read between the
  // two writes so the browser can't coalesce them (the leaderboard's collapseDetail precedent).
  panel.style.maxHeight = `${panel.scrollHeight}px`;
  panel.offsetHeight; // force layout: commit the pinned height before the drop to 0
  panel.classList.remove("mp-section-panel--open");
  panel.style.maxHeight = "0px";
}

/** Derives `.mp-panel-body` — the fetch/nudge/error mount target since the two-child panel
 *  restructure (part B) — and dispatches on registration. `panel` (not `body`) stays the
 *  parameter every caller passes: `buildNudge`/`buildNudgeCta` below close over `panel` so their
 *  success callback can re-invoke this same function, and `expand`/`settleOpen` measure the
 *  panel node as a whole (both children). */
function refreshPanel(panel, bar, refreshNameSlot) {
  const body = panel.querySelector(".mp-panel-body");
  return getRegisteredName() != null
    ? refreshRegisteredPanel(body)
    : refreshUnregisteredPanel(panel, body, bar, refreshNameSlot);
}

function refreshRegisteredPanel(body) {
  const seq = ++fetchSeq; // stale-response guard: a superseded double-toggle's fetch no-ops below
  return fetchFragment("/multiplayer/tables-panel")
    .then((html) => {
      if (seq !== fetchSeq) return; // a later refresh already won
      mountFragment(body, html);
      enhanceTables(body, { onEnter: enterRun });
      body.appendChild(buildCreateRow());
    })
    .catch((e) => {
      if (seq !== fetchSeq) return;
      console.warn(
        "[game-start-multiplayer-section] tables fetch failed:",
        e?.message ?? e,
      );
      body.replaceChildren(buildErrorLine());
    });
}

function refreshUnregisteredPanel(panel, body, bar, refreshNameSlot) {
  body.replaceChildren(buildNudge(panel, bar, refreshNameSlot));
  return Promise.resolve();
}

/** The unregistered branch: a nudge line + a CTA opening the identity overlay (design § "The
 *  section" — reuses the doorway this screen already has rather than a second inline form). */
function buildNudge(panel, bar, refreshNameSlot) {
  const wrap = document.createElement("div");
  wrap.className = "mp-section-nudge";
  const line = document.createElement("p");
  line.className = "mp-section-nudge-text";
  line.textContent = t("multiplayerSectionNudge");
  wrap.appendChild(line);
  wrap.appendChild(buildNudgeCta(panel, bar, refreshNameSlot));
  return wrap;
}

/** The nudge's Register CTA — icon + label + click wiring, split out of `buildNudge` on size
 *  (review finding). The overlay's success callback refetches the panel in place, so tables
 *  appear post-registration with no reload — and calls `refreshNameSlot` (the name slot's own
 *  `renderState`, threaded down from `buildGameStartContent`) first, since a register from here
 *  reaches `openProfileOverlay` with this callback as `rerenderSlot`, not the name slot's own:
 *  without it the slot above the section would keep showing the anonymous row until the next
 *  full render (review finding). */
function buildNudgeCta(panel, bar, refreshNameSlot) {
  const cta = document.createElement("button");
  cta.type = "button";
  cta.className = "btn btn-primary mp-section-nudge-cta";
  // Andre 2026-08-10 (acceptance round): the Knight — mono currentColor (createPersonaKnight's own
  // neutral default), not the filled IDENTITY_COLOR.human variant the name-slot's persona bust
  // (game-start-ui.js#buildNamePersona) and the account overlay use for an already-registered
  // identity. (The pause menu's own identity entry no longer wears the Knight at all — nav-icon-
  // candidate-placements task, game-menu.js#identityEntry now wears the crossed keys instead — so
  // this mono cut no longer matches a sibling mono Knight elsewhere; it stands on its own reasoning
  // below.) Tension worth naming: ICONOGRAPHY.md § Persona Icons records the Knight as
  // reveal-when-earned — it marks the registered identity and its first appearance is the
  // register/login confirmation etch (game-start-identity-confirmation.js#playPersonaEtch).
  // Putting it on the pre-registration CTA spends that "first appearance" a beat early. Andre
  // asked for it directly and the read holds regardless: the button is the doorway to becoming
  // that Knight, so seeing the mark here first, then watching it etch in as *you* on success, is
  // a coherent arc, not a broken one.
  const ctaIcon = createPersonaKnight(NUDGE_CTA_ICON_SIZE);
  ctaIcon.classList.add("mp-section-nudge-cta-icon");
  ctaIcon.setAttribute("aria-hidden", "true"); // decorative — the button's own text is the accessible name
  cta.appendChild(ctaIcon);
  cta.appendChild(document.createTextNode(t("multiplayerSectionNudgeCta")));
  // The opener is the leaf, not the overlay module: it supplies the screen's ONE live settings
  // object, so a rename made here reaches what Play sends (profile-overlay-and-menu-slim
  // § Settings ownership — this nudge used to hand the overlay a throwaway snapshot).
  cta.addEventListener("click", () => {
    openProfileOverlay(
      () => {
        refreshNameSlot();
        refreshPanel(panel, bar, refreshNameSlot).then(() => {
          if (isOpen(bar)) settleOpen(panel);
        });
      },
      { screen: "start", source: "multiplayer-nudge" },
    );
  });
  return cta;
}

// Section-owned, client-built (not template markup — see the file header). Reuses the existing
// rung-5 "Open a new table" copy (`multiplayerOpenTable`) rather than minting a duplicate key with
// the identical text — both as the row's own label and the overlay's header title.
function buildCreateRow() {
  const row = document.createElement("button");
  row.type = "button";
  row.className = "mp-section-create-row";
  row.textContent = t("multiplayerOpenTable");
  row.addEventListener("click", openCreateOverlay);
  return row;
}

/** The dashed row's target since PR 2 (design § "Create a table"): a vellum overlay fetching the
 *  bare `multiplayer :: create` fragment and wiring it with `wireCreateForm` — success dismisses
 *  the overlay and `enterRun`s the fresh table in-page (better than the standalone page's
 *  navigate), a 409 falls through to `wireCreateForm`'s own exists-message handling, carried over
 *  from the extraction. */
function openCreateOverlay() {
  const { panel, dismiss } = openVellumOverlay("mp-create-overlay-panel", {
    header: { title: t("multiplayerOpenTable") },
  });
  const content = document.createElement("div");
  content.className = "overlay-fragment-content";
  panel.appendChild(content);
  fetchFragment("/multiplayer/create-panel")
    .then((html) => {
      mountFragment(content, html);
      // The shell header above already carries this title (games-overlay.js's own h1-strip
      // precedent) — the fragment's own <h2>Open a new table</h2> (multiplayer.html's `::
      // create`) would otherwise duplicate it right inside the overlay body.
      content.querySelector("h2")?.remove();
      wireCreateForm(content, {
        onCreated: (newGameId) => {
          dismiss();
          // "create", not the enterRun default "card" — a freshly created table wasn't reached by
          // clicking an existing run card; table-created (wireCreateForm) already fires its own
          // event — this is the separate table-entered funnel dimension (root-level call).
          enterRun(newGameId, "create");
        },
      });
    })
    .catch((e) => {
      console.warn(
        "[game-start-multiplayer-section] create-panel fetch failed:",
        e?.message ?? e,
      );
      content.replaceChildren(buildErrorLine());
    });
}

// Reuses the standalone page's generic failure copy (`multiplayerCreateFailed`) rather than a
// bespoke line — the fetch that failed here is the same kind of "that didn't work" as the page's.
function buildErrorLine() {
  const line = document.createElement("p");
  line.className = "mp-section-error";
  line.textContent = t("multiplayerCreateFailed");
  return line;
}

function buildChevron() {
  const chevron = document.createElement("span");
  chevron.className = "mp-section-chevron";
  chevron.setAttribute("aria-hidden", "true");
  chevron.textContent = "›"; // › — flips to point down via CSS on aria-expanded="true"
  return chevron;
}

function buildBar() {
  const bar = document.createElement("button");
  bar.type = "button";
  // `.mp-run` — the run-card family's own vellum material, reused deliberately (design § "The
  // section": "the bar is the family's doorway"); `.mp-section-bar` carries this bar's own layout
  // (mark + label + chevron spread) and is the entrance's ROWS-group frame-etch target
  // (game-start-toggles-etch.js#addTogglesEtch).
  bar.className = "mp-run mp-section-bar";
  bar.setAttribute("aria-expanded", "false");
  bar.setAttribute("aria-controls", "mp-section-panel");
  const mark = document.createElement("span");
  mark.className = "mp-section-mark";
  mark.setAttribute("aria-hidden", "true");
  // Ink-basis check (spec § "Multiplayer bar joins the strip's scale", docs/LAYOUT.md's basis
  // rule): the stool's own ink-to-box ratio differs from the strip icons' — verified at this
  // size, live screenshot + path-geometry (stool ink ~28.4 of 48 viewBox units vs. the strip
  // family's ~26.5, ~7% taller) — close enough to read level at a glance; no static compensation
  // factor needed.
  mark.appendChild(createThreeLeggedStool(MARK_BUILD_SIZE));
  bar.appendChild(mark);
  const label = document.createElement("span");
  label.className = "mp-section-label";
  label.textContent = t("multiplayerSectionLabel");
  bar.appendChild(label);
  bar.appendChild(buildChevron());
  return bar;
}

/** The two permanent children (part B): `.mp-panel-notice` (the turn-banner block's home,
 *  touched only by `applyTurns` below) and `.mp-panel-body` (the fetch/nudge/error mount
 *  target, touched only by `refreshPanel`'s two branches) — so a tables refresh can never wipe
 *  the notice, and the notice can never wipe a mounted table list (the round-1 blocking finding
 *  the old single-node panel couldn't satisfy: every existing content path wiped its mount
 *  target outright). */
function buildPanel() {
  const panel = document.createElement("div");
  // Derived from MULTIPLAYER_PANEL_SELECTOR (a leading-dot class selector), not a separate
  // literal — measureMenuH's exclusion reads that exported selector, so a rename here must move
  // it too, not silently desync (review finding: the two used to be independent literals).
  panel.className = MULTIPLAYER_PANEL_SELECTOR.slice(1);
  panel.id = "mp-section-panel";
  panel.setAttribute("aria-hidden", "true");
  const notice = document.createElement("div");
  notice.className = "mp-panel-notice";
  const body = document.createElement("div");
  body.className = "mp-panel-body";
  panel.appendChild(notice);
  panel.appendChild(body);
  return panel;
}

/** Sets/clears the bar's turn-count badge (design § B, shape-live look/placement — a small
 *  amber count disc between the label and the chevron is the starting cut). Find-or-create/
 *  remove, the `hint-button.js` shape: `count < 1` removes any prior badge outright rather than
 *  hiding it at 0, mirroring the "visible unconditionally when count >= 1" contract in reverse.
 *
 *  The bar's `aria-label` rides along, because the badge is the whole signal for a collapsed
 *  section and a bare digit doesn't carry it: the banners themselves sit inside an `aria-hidden`
 *  panel until the viewer opens it, so without this the accessible name reads "Multiplayer 1".
 *  The sentence is `game-start-turn-banners.js#turnCountSentence` — the same wording the block's
 *  own headline uses, so the announcement can't drift from what opening the section reveals.
 *  Removed again at `count < 1`, so the name falls back to the label's own text. */
function applyBadge(bar, count) {
  let badge = bar.querySelector(".mp-section-badge");
  if (count < 1) {
    badge?.remove();
    bar.removeAttribute("aria-label");
    return;
  }
  if (badge == null) {
    badge = document.createElement("span");
    badge.className = "mp-section-badge";
    bar.insertBefore(badge, bar.querySelector(".mp-section-chevron"));
  }
  badge.textContent = String(count);
  bar.setAttribute(
    "aria-label",
    `${t("multiplayerSectionLabel")}, ${turnCountSentence(count)}`,
  );
}

/** The turn-notification flow (part B), run once at build — `pristine` from `readStampTurnRuns()`
 *  synchronously, non-pristine after `fetchTurnRuns()` resolves (both call sites below). Mounts
 *  the banner block into `.mp-panel-notice` (replacing any prior block — a rebuild is the only
 *  caller shape, never an append) and sets the bar badge unconditionally on count (open or
 *  collapsed — its job is signaling a collapsed section, and showing it open too costs nothing).
 *  It never opens the section itself: a collapsed section stays collapsed and lets the badge do
 *  the signaling (file header). When it IS already open — a Menu-forced mount, or the viewer
 *  opened the bar before the fetch landed — the mount above still needs a `settleOpen`
 *  re-measure: the panel's max-height may still be a pinned pixel value from an `expand()` cycle
 *  mid-flight, and that in-flight transition needs to include the notice content. `settleOpen`
 *  itself no-ops when the panel is already settled at `max-height: none` (the steady state a
 *  slower non-pristine fetch resolves into) — see its own header for why a re-pin there would
 *  strand the cap. */
function applyTurns(bar, panel, turns, pristine) {
  const notice = panel.querySelector(".mp-panel-notice");
  notice.replaceChildren();
  const block = buildTurnBannerBlock(turns);
  if (block != null) {
    // A plain CSS one-shot fade (game-start-turn-banners.js's own file header), never a
    // game-start-entrance.js group — banners are not entrance participants. Only the
    // rebuilt, non-pristine block fades; the pristine block sits with the rest of the
    // screen at rest from the first paint.
    if (!pristine) block.classList.add("game-start-turn-banners--reveal");
    notice.appendChild(block);
  }
  applyBadge(bar, turns.length);
  if (turns.length > 0 && isOpen(bar)) settleOpen(panel);
}

/** The turn-notification flow's data-source split (part B) — `readStampTurnRuns()` synchronously
 *  on the pristine first paint, `fetchTurnRuns()` on every later render — split out of
 *  `buildMultiplayerSection` on size (function-budget, the single-caller coherent-concept
 *  carve-out). */
function primeTurnNotifications(bar, panel, pristine) {
  if (pristine) {
    applyTurns(bar, panel, readStampTurnRuns(), true);
    return;
  }
  fetchTurnRuns().then((turns) => applyTurns(bar, panel, turns, false));
}

/** Builds the section: the bar + its two-child expansion panel, wired to toggle on click and to
 *  honor a pending `requestMultiplayerSectionOpen()` one-shot on mount — the section's only
 *  non-click way open, and a deliberate control interaction like the click itself, so it fetches
 *  the panel body's content and fires `multiplayer-section-opened` the same way. Mounted by
 *  `game-start-ui.js#buildGameStartContent` between the mode cards and the control bank.
 *  `refreshNameSlot` is the name slot's own `renderState`, threaded down for the unregistered
 *  branch's nudge (§ "buildNudge" above) — the one path here that can change registration state
 *  while the slot is on screen. `pristine` (part B, start-screen-layout-improvements) picks the
 *  turn-notification flow's data source — `readStampTurnRuns()` synchronously on the pristine
 *  first paint, `fetchTurnRuns()` on every later render — mirroring the same `pristine` param
 *  `buildGameStartContent` itself receives from `renderGameStart`. */
export function buildMultiplayerSection(refreshNameSlot, pristine) {
  const section = document.createElement("div");
  section.className = "mp-section";
  const bar = buildBar();
  const panel = buildPanel();
  section.appendChild(bar);
  section.appendChild(panel);

  bar.addEventListener("click", () => {
    if (isOpen(bar)) collapse(bar, panel);
    else expand(bar, panel, refreshNameSlot, "bar");
  });

  const forcedOpen = forceOpenOnce;
  forceOpenOnce = false;
  if (forcedOpen) expand(bar, panel, refreshNameSlot, "menu");

  primeTurnNotifications(bar, panel, pristine);

  return section;
}
